import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface ControlDocsFolder {
  id: number;
  tenant_id: number;
  parent_folder_id?: number | null;
  name: string;
  visibility: 'PUBLIC' | 'PRIVATE';
  is_favorite?: number | boolean;
  created_by?: number;
  created_by_name?: string;
  item_count?: number;
  latest_file_id?: number | null;
  latest_file_type?: string | null;
  latest_file_name?: string | null;
  latest_file_path?: string | null;
  has_share_link?: number | boolean;
  created_at: string;
  deleted_at?: string | null;
  deleted_by?: number | null;
  restore_available_until?: string | null;
  admin_only_at?: string | null;
  restore_parent_name?: string | null;
}

export interface ControlDocsFile {
  id: number;
  tenant_id: number;
  folder_id: number;
  name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  visibility: 'PUBLIC' | 'PRIVATE';
  uploaded_by?: number;
  uploaded_by_name?: string;
  uploaded_at: string;
  has_share_link?: number | boolean;
  last_accessed_at?: string;
  deleted_at?: string | null;
  deleted_by?: number | null;
  restore_available_until?: string | null;
  admin_only_at?: string | null;
  restore_parent_name?: string | null;
}

export interface ControlDocsActivity {
  id: number;
  action_type: string;
  target_type: 'FOLDER' | 'FILE' | 'SYSTEM';
  target_id?: number | null;
  target_name?: string | null;
  meta_json?: any;
  created_at: string;
}

export interface ControlDocsDashboard {
  recent_folders: ControlDocsFolder[];
  recent_files: ControlDocsFile[];
  recent_activities: ControlDocsActivity[];
}

export interface ControlDocsWorkspaceItems {
  folders: ControlDocsFolder[];
  files: ControlDocsFile[];
}

export type ControlDocsWorkspaceMode = 'owned' | 'shared';

export interface ControlDocsTrashItems {
  folders: ControlDocsFolder[];
  files: ControlDocsFile[];
}

export interface ShareLinkFileInfo {
  id?: number;
  name: string;
  file_type?: string;
  file_size?: number;
  url: string;
}

export interface ShareLinkAccessResponse {
  type: 'FILE' | 'FOLDER';
  file?: ShareLinkFileInfo;
  folder?: { id: number; name: string };
  files?: ShareLinkFileInfo[];
  subfolders?: { id: number; name: string; visibility?: 'PUBLIC' | 'PRIVATE' }[];
  breadcrumb?: { id: number; name: string }[];
}

@Injectable({ providedIn: 'root' })
export class ControlDocsService {
  private readonly baseUrl = `${API_BASE_URL}/api/controldocs`;

  constructor(private http: HttpClient) {}

  listFolders(tenantId: number, userId: number, parentId?: number | null): Observable<ControlDocsFolder[]> {
    let params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    if (parentId !== undefined) {
      params = params.set('parent_id', parentId ?? '');
    }
    return this.http.get<ControlDocsFolder[]>(`${this.baseUrl}/folders`, { params });
  }

  createFolder(payload: {
    tenant_id: number;
    created_by: number;
    name: string;
    visibility: 'PUBLIC' | 'PRIVATE';
    parent_folder_id?: number | null;
  }): Observable<ControlDocsFolder> {
    return this.http.post<ControlDocsFolder>(`${this.baseUrl}/folders`, payload);
  }

  listFiles(tenantId: number, userId: number, folderId: number): Observable<ControlDocsFile[]> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId)
      .set('folder_id', folderId);
    return this.http.get<ControlDocsFile[]>(`${this.baseUrl}/files`, { params });
  }

  uploadFile(payload: {
    tenant_id: number;
    folder_id?: number | null;
    uploaded_by: number;
    file: File;
    visibility?: 'PUBLIC' | 'PRIVATE';
  }): Observable<ControlDocsFile> {
    const formData = new FormData();
    formData.append('tenant_id', String(payload.tenant_id));
    formData.append('uploaded_by', String(payload.uploaded_by));
    formData.append('folder_id', payload.folder_id ? String(payload.folder_id) : '');
    formData.append('visibility', payload.visibility || 'PRIVATE');
    formData.append('file', payload.file);
    return this.http.post<ControlDocsFile>(`${this.baseUrl}/files`, formData);
  }

  deleteFolder(folderId: number, tenantId: number, userId?: number): Observable<void> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http.delete<void>(`${this.baseUrl}/folders/${folderId}`, { params });
  }

  deleteFile(fileId: number, tenantId: number, userId?: number): Observable<void> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http.delete<void>(`${this.baseUrl}/files/${fileId}`, { params });
  }

  setFolderFavorite(folderId: number, payload: { tenant_id: number; user_id: number; is_favorite: boolean }): Observable<{ success: boolean; is_favorite: number }> {
    return this.http.patch<{ success: boolean; is_favorite: number }>(`${this.baseUrl}/folders/${folderId}/favorite`, payload);
  }

  getDashboard(tenantId: number, userId: number): Observable<ControlDocsDashboard> {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlDocsDashboard>(`${this.baseUrl}/dashboard`, { params });
  }

  getWorkspaceItems(tenantId: number, userId: number, mode: ControlDocsWorkspaceMode = 'owned'): Observable<ControlDocsWorkspaceItems> {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId).set('mode', mode);
    return this.http.get<ControlDocsWorkspaceItems>(`${this.baseUrl}/workspace-items`, { params });
  }

  getFavoriteFolders(tenantId: number, userId: number): Observable<ControlDocsFolder[]> {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.get<ControlDocsFolder[]>(`${this.baseUrl}/favorite-folders`, { params });
  }

  getTrashItems(tenantId: number, userId: number, parentId?: number | null): Observable<ControlDocsTrashItems> {
    let params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    if (parentId !== undefined) {
      params = params.set('parent_id', parentId ?? '');
    }
    return this.http.get<ControlDocsTrashItems>(`${this.baseUrl}/trash-items`, { params });
  }

  restoreFolder(folderId: number, payload: { tenant_id: number; user_id: number }): Observable<{ success: boolean; restored: boolean }> {
    return this.http.patch<{ success: boolean; restored: boolean }>(`${this.baseUrl}/folders/${folderId}/restore`, payload);
  }

  restoreFile(fileId: number, payload: { tenant_id: number; user_id: number }): Observable<{ success: boolean; restored: boolean }> {
    return this.http.patch<{ success: boolean; restored: boolean }>(`${this.baseUrl}/files/${fileId}/restore`, payload);
  }

  deleteFolderPermanently(folderId: number, tenantId: number, userId: number): Observable<{ success: boolean; deleted: boolean }> {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete<{ success: boolean; deleted: boolean }>(`${this.baseUrl}/folders/${folderId}/permanent`, { params });
  }

  deleteFilePermanently(fileId: number, tenantId: number, userId: number): Observable<{ success: boolean; deleted: boolean }> {
    const params = new HttpParams().set('tenant_id', tenantId).set('user_id', userId);
    return this.http.delete<{ success: boolean; deleted: boolean }>(`${this.baseUrl}/files/${fileId}/permanent`, { params });
  }

  logActivity(payload: {
    tenant_id: number;
    user_id: number;
    action_type: string;
    target_type: 'FOLDER' | 'FILE' | 'SYSTEM';
    target_id?: number | null;
    target_name?: string | null;
    meta?: Record<string, any>;
  }): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.baseUrl}/activity`, payload);
  }

  downloadUrl(fileId: number, tenantId: number, userId: number): string {
    return `${this.baseUrl}/files/${fileId}/download?tenant_id=${tenantId}&user_id=${userId}`;
  }

  viewUrl(fileId: number, tenantId: number, userId: number): string {
    return `${this.baseUrl}/files/${fileId}/view?tenant_id=${tenantId}&user_id=${userId}`;
  }

  createShareLink(payload: {
    tenant_id: number;
    created_by: number;
    target_type: 'FILE' | 'FOLDER';
    target_id: number;
    password?: string;
    expires_at?: string;
  }): Observable<{ access_token: string; password?: string }> {
    return this.http.post<{ access_token: string; password?: string }>(`${this.baseUrl}/share-links`, payload);
  }

  getShareLinkByTarget(
    tenantId: number,
    userId: number,
    targetType: 'FILE' | 'FOLDER',
    targetId: number
  ): Observable<{ id: number | null; access_token: string | null; expires_at: string | null; has_password: boolean; is_active: boolean; current_password: string | null }> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId)
      .set('target_type', targetType)
      .set('target_id', targetId);
    return this.http.get<{ id: number | null; access_token: string | null; expires_at: string | null; has_password: boolean; is_active: boolean; current_password: string | null }>(
      `${this.baseUrl}/share-links/by-target`,
      { params }
    );
  }

  updateShareLink(
    shareLinkId: number,
      payload: {
      tenant_id: number;
      user_id: number;
      password_enabled: boolean;
      password?: string;
      expires_at?: string | null;
      is_active?: boolean;
    }
  ): Observable<{ id: number; access_token: string; expires_at: string | null; has_password: boolean; is_active: boolean; current_password: string | null }> {
    return this.http.patch<{ id: number; access_token: string; expires_at: string | null; has_password: boolean; is_active: boolean; current_password: string | null }>(
      `${this.baseUrl}/share-links/${shareLinkId}`,
      payload
    );
  }

  getAccessGrants(
    tenantId: number,
    userId: number,
    targetType: 'FILE' | 'FOLDER',
    targetId: number
  ): Observable<{ emails: string[] }> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('user_id', userId)
      .set('target_type', targetType)
      .set('target_id', targetId);
    return this.http.get<{ emails: string[] }>(`${this.baseUrl}/access-grants`, { params });
  }

  saveAccessGrants(payload: {
    tenant_id: number;
    user_id: number;
    target_type: 'FILE' | 'FOLDER';
    target_id: number;
    emails: string[];
  }): Observable<{ success: boolean; emails: string[] }> {
    return this.http.put<{ success: boolean; emails: string[] }>(`${this.baseUrl}/access-grants`, payload);
  }

  updateFolderVisibility(
    folderId: number,
    payload: { tenant_id: number; user_id: number; visibility: 'PUBLIC' | 'PRIVATE' }
  ): Observable<{ success: boolean; visibility: 'PUBLIC' | 'PRIVATE' }> {
    return this.http.patch<{ success: boolean; visibility: 'PUBLIC' | 'PRIVATE' }>(
      `${this.baseUrl}/folders/${folderId}/visibility`,
      payload
    );
  }

  renameFolder(
    folderId: number,
    payload: { tenant_id: number; user_id: number; name: string }
  ): Observable<{ success: boolean; name: string }> {
    return this.http.patch<{ success: boolean; name: string }>(
      `${this.baseUrl}/folders/${folderId}/name`,
      payload
    );
  }

  updateFileVisibility(
    fileId: number,
    payload: { tenant_id: number; user_id: number; visibility: 'PUBLIC' | 'PRIVATE' }
  ): Observable<{ success: boolean; visibility: 'PUBLIC' | 'PRIVATE' }> {
    return this.http.patch<{ success: boolean; visibility: 'PUBLIC' | 'PRIVATE' }>(
      `${this.baseUrl}/files/${fileId}/visibility`,
      payload
    );
  }

  renameFile(
    fileId: number,
    payload: { tenant_id: number; user_id: number; name: string }
  ): Observable<{ success: boolean; name: string }> {
    return this.http.patch<{ success: boolean; name: string }>(
      `${this.baseUrl}/files/${fileId}/name`,
      payload
    );
  }

  moveFile(
    fileId: number,
    payload: { tenant_id: number; user_id: number; target_folder_id: number }
  ): Observable<{ success: boolean; folder_id: number }> {
    return this.http.patch<{ success: boolean; folder_id: number }>(
      `${this.baseUrl}/files/${fileId}/move`,
      payload
    );
  }

  accessShareLink(token: string, password?: string, folderId?: number): Observable<ShareLinkAccessResponse> {
    const payload: { password?: string; folder_id?: number } = {};
    if (password) {
      payload.password = password;
    }
    if (folderId !== undefined) {
      payload.folder_id = folderId;
    }
    return this.http.post<ShareLinkAccessResponse>(`${this.baseUrl}/share-links/${token}/access`, payload);
  }
}
